import { Link } from 'react-router-dom';
import { SeoHead, organizationLd, webSiteLd, webPageLd } from '../seo';
import BangaloreMonthly from './guides/BangaloreMonthly';
import DelhiMetroParking from './guides/DelhiMetroParking';
import EVChargingGuide from './guides/EVChargingGuide';
import NegotiateParking from './guides/NegotiateParking';
import TwoWheelerSafety from './guides/TwoWheelerSafety';
import AirportParking from './guides/AirportParking';
import ApartmentVisitor from './guides/ApartmentVisitor';
import CommercialOperator from './guides/CommercialOperator';
import CommercialVsAggregator from './guides/CommercialVsAggregator';
import SeekerStrategy from './guides/SeekerStrategy';

const GUIDES = [
  {
    slug: 'how-to-find-monthly-parking-in-bangalore',
    title: 'How to Find Monthly Parking in Bangalore Without Paying Brokerage',
    description: 'A step-by-step Bangalore-specific guide to finding monthly car, bike, and scooter parking.',
    readMinutes: 9,
    category: 'cities',
    component: BangaloreMonthly,
  },
  {
    slug: 'parking-near-delhi-metro-stations',
    title: 'Parking Near Delhi Metro Stations: A Complete Park-and-Ride Guide',
    description: 'Every Delhi Metro station with reliable two-wheeler and car parking, organised by line.',
    readMinutes: 11,
    category: 'commute',
    component: DelhiMetroParking,
  },
  {
    slug: 'ev-charging-parking-guide-india',
    title: "The EV Owner's Parking Guide: How to Find a Spot That Actually Charges",
    description: 'How to find an EV charging parking spot in India that is not ICE-d, broken, or 2 km from your route.',
    readMinutes: 8,
    category: 'ev',
    component: EVChargingGuide,
  },
  {
    slug: 'negotiate-parking-rent-india',
    title: 'How to Negotiate Parking Rent in India (and What to Say)',
    description: 'Scripts, tactics, and the data to back you up when negotiating monthly parking rent.',
    readMinutes: 7,
    category: 'parking-tips',
    component: NegotiateParking,
  },
  {
    slug: 'safe-parking-tips-two-wheelers',
    title: 'Two-Wheeler Parking Safety in India: A Practical Guide',
    description: 'Anti-theft tips, what to look for in a covered two-wheeler spot, and how to reduce towing and theft risk.',
    readMinutes: 6,
    category: 'two-wheeler',
    component: TwoWheelerSafety,
  },
  {
    slug: 'airport-parking-mumbai-bangalore-delhi',
    title: 'Airport Parking in Mumbai, Bangalore, and Delhi: A City-by-City Guide',
    description: 'Long-stay airport parking rates, alternatives near each major Indian airport.',
    readMinutes: 10,
    category: 'airport',
    component: AirportParking,
  },
  {
    slug: 'apartment-visitor-parking-solutions',
    title: 'How RWAs and Residents Can Solve the Visitor Parking Problem',
    description: 'Why apartment visitor parking is the #1 source of resident-vs-RWA fights, and 4 practical solutions.',
    readMinutes: 7,
    category: 'parking-tips',
    component: ApartmentVisitor,
  },
  {
    slug: 'commercial-parking-business-guide-india',
    title: 'Starting a Commercial Parking Lot in India: A Practical Guide',
    description: 'How to start, license, and operate a paid commercial parking lot in an Indian metro.',
    readMinutes: 12,
    category: 'operator',
    component: CommercialOperator,
  },
  {
    slug: 'commercial-vs-aggregator',
    title: 'Commercial Operators vs Aggregator Platforms: A Side-by-Side Look',
    description: 'Park+, NoBroker Parking, JustPark — how the commission model compares to free open-data listing.',
    readMinutes: 6,
    category: 'operator',
    component: CommercialVsAggregator,
  },
  {
    slug: 'seeker-strategy-india',
    title: "A Seeker's Complete Strategy for Finding Parking in India",
    description: 'The end-to-end seeker playbook: from dropping your first pin to locking in a long-term spot.',
    readMinutes: 8,
    category: 'parking-tips',
    component: SeekerStrategy,
  },
];

export default function GuidesIndex() {
  return (
    <>
      <SeoHead
        title="Parking Guides for India — Long-Form Playbooks | Parksy"
        description="Long-form guides for finding, listing, and operating parking in India. Bangalore monthly parking, Delhi Metro park-and-ride, EV charging, two-wheeler safety, airport parking, and more."
        canonical="https://parksy.rent/guides"
        ogImage="https://parksy.rent/og/guides.png"
        schemas={[organizationLd(), webSiteLd(), webPageLd({ name: 'Parksy Guides', description: 'Long-form guides for parking in India.', url: 'https://parksy.rent/guides' })]}
      />
      <div className="max-w-4xl mx-auto px-4 py-10">
        <header className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Parking guides for India</h1>
          <p className="text-lg text-gray-700">
            Long-form playbooks for finding, listing, and operating parking across Indian
            metros. Updated quarterly. Data-driven, plain English, no broker jargon.
          </p>
        </header>

        <div className="grid gap-4">
          {GUIDES.map((g) => (
            <Link
              key={g.slug}
              to={`/guides/${g.slug}`}
              className="block bg-white border rounded-xl p-5 hover:border-parksy-500 transition"
            >
              <div className="text-xs uppercase tracking-wide text-parksy-700 font-semibold mb-1">
                {g.category.replace(/-/g, ' ')}
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">{g.title}</h2>
              <p className="text-sm text-gray-700 mb-2">{g.description}</p>
              <div className="text-xs text-gray-500">{g.readMinutes} min read</div>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}
